import { useRef, type ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@shared/lib/cn'
import { Button, ButtonVariant } from '../Button'
import { Dialog } from '../Dialog/Dialog'

export interface ResultDialogAction {
  label: string
  onClick: () => void
  variant?: ButtonVariant
  /** Фокус при открытии окна — на этой кнопке (по умолчанию Radix берёт первую).
   *  Главное действие стоит на обычном месте, а фокус ему отдаётся флагом. */
  autoFocus?: boolean
}

export interface ResultDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Картинка-состояние 200×200 из макета. Пока иллюстраций нет, сюда кладут
   *  крупную иконку — слот один и тот же. */
  visual: ReactNode
  /** Размер слота под визуал. По умолчанию 200×200 — размер растровых
   *  иллюстраций. У модалок с векторной иконкой он меньше (80×80 в
   *  537:21545), и фиксированный бокс добавил бы им 120px пустоты. */
  visualClassName?: string
  title: string
  /** Пояснение под заголовком. `ReactNode` — потому что у части модалок под
   *  текстом идёт ещё и предупреждение со своим оформлением. */
  text?: ReactNode
  /** Кнопки под текстом: у успеха одна, у отказа — действие и «Закрыть». */
  actions: ResultDialogAction[]
  className?: string
  /** `false` — без крестика, Esc и клик мимо не закрывают: окно закрывается только
   *  своими кнопками. Для обязательных уведомлений. */
  dismissible?: boolean
}

/**
 * Модалка результата: иллюстрация, заголовок, пояснение и кнопки.
 *
 * Макеты участника (2.7 «Оплата прошла», 2.9 «Вы зарегистрированы!», 2.4
 * «Недостаточно средств») устроены одинаково и отличаются только содержимым,
 * поэтому компонент один. До неё эти три исхода показывались тостом — и вместе
 * с тостом терялось действие: у «Недостаточно средств» в макете есть кнопка
 * «Пополнить баланс», а тост её показать не мог.
 */
export function ResultDialog({
  open,
  onOpenChange,
  visual,
  visualClassName = 'size-50',
  title,
  text,
  actions,
  className,
  dismissible = true,
}: ResultDialogProps) {
  const actionsRef = useRef<HTMLDivElement>(null)

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      dismissible={dismissible}
      // Название и текст рисуем сами через Title/Description: окно получает имя и
      // описание от них, а не запасные.
      hasOwnTitle
      hasOwnDescription
      onOpenAutoFocus={(event) => {
        const target = actionsRef.current?.querySelector<HTMLElement>('[data-autofocus]')
        if (!target) return
        event.preventDefault()
        target.focus()
      }}
      className={cn('max-w-114', className)}
    >
      <div className="flex flex-col items-center gap-6 text-center">
        <div className={cn('flex items-center justify-center', visualClassName)}>{visual}</div>

        <div className="flex flex-col gap-2">
          <DialogPrimitive.Title className="typo-biggest text-foreground">
            {title}
          </DialogPrimitive.Title>
          {text ? (
            <DialogPrimitive.Description asChild>
              <div className="typo-base text-secondary">{text}</div>
            </DialogPrimitive.Description>
          ) : (
            // Без текста описанием служит заголовок, как у запасного в `Dialog`.
            <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
          )}
        </div>

        {/* Кнопки делят ширину поровну — так во всех трёх макетах, включая
            одиночную кнопку на всю строку (сверено 2026-08-21: 495:134202,
            495:128490, 537:21545). До этого растягивалась только первая, и
            «Пополнить баланс» выходил вдвое шире «Закрыть». На узком экране
            строка переносится. */}
        <div ref={actionsRef} className="flex w-full flex-wrap items-center gap-4">
          {actions.map((action) => (
            <Button
              key={action.label}
              type="button"
              variant={action.variant ?? ButtonVariant.primary}
              className="min-w-40 flex-1"
              data-autofocus={action.autoFocus ? '' : undefined}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </div>
      </div>
    </Dialog>
  )
}
